import AppStoreBadge from '../components/AppStoreBadge';

// Feature sections with colors matching app areas
const features = [
  {
    title: 'Build Reports for AI',
    desc: 'Every build creates a structured report with errors, warnings, and code context. Toggle between visual cards for you or formatted text for AI assistants. Export everything with one click.',
    color: 'red', // Build errors are red in the app
    gradient: 'from-red-500/20 to-red-600/10',
    accentColor: 'text-red-400',
    borderColor: 'border-red-400/20',
  },
  {
    title: 'SwiftLint Integration',
    desc: 'Run code quality scans and see violations in a beautiful dashboard. Export results formatted for ChatGPT or Claude to get instant fix suggestions.',
    color: 'purple', // SwiftLint uses purple in the app
    gradient: 'from-accent-purple-500/20 to-accent-purple-600/10',
    accentColor: 'text-accent-purple-400',
    borderColor: 'border-accent-purple-400/20',
  },
  {
    title: 'Live Build Output',
    desc: 'Watch your builds in real time with color-coded logs. Errors are red, warnings are yellow, success is green. Search through output instantly.',
    color: 'emerald', // Terminal/success is green
    gradient: 'from-accent-emerald-500/20 to-accent-emerald-600/10',
    accentColor: 'text-accent-emerald-400',
    borderColor: 'border-accent-emerald-400/20',
  },
  {
    title: 'Smart Test Results',
    desc: 'See which tests passed and failed at a glance. Failed tests include the context AI needs to suggest fixes. Debug faster with structured results.',
    color: 'blue', // Tests use brand blue
    gradient: 'from-brand-500/20 to-brand-600/10',
    accentColor: 'text-brand-400',
    borderColor: 'border-brand-400/20',
  },
  {
    title: 'Project Intelligence',
    desc: 'Get instant insights into your codebase: architecture patterns, dependencies, code quality metrics, and more. Perfect for onboarding AI assistants to your project.',
    color: 'amber', // Intelligence/insights use amber
    gradient: 'from-accent-amber-500/20 to-accent-amber-600/10',
    accentColor: 'text-accent-amber-400',
    borderColor: 'border-accent-amber-400/20',
  },
  {
    title: 'Simulator Management',
    desc: 'See all your simulators at a glance with health status. Get warnings about issues before you build. No more "destination not available" surprises.',
    color: 'cyan', // Simulators use cyan
    gradient: 'from-cyan-500/20 to-cyan-600/10',
    accentColor: 'text-cyan-400',
    borderColor: 'border-cyan-400/20',
  },
  {
    title: 'One-Click Debug Export',
    desc: 'Export all issues from builds, tests, and linting in one comprehensive report. Share with your team or paste into AI assistants for help.',
    color: 'violet', // Export/reports use violet
    gradient: 'from-violet-500/20 to-violet-600/10',
    accentColor: 'text-violet-400',
    borderColor: 'border-violet-400/20',
  },
  {
    title: 'Build History',
    desc: 'Track every build with status, duration, and results. Jump back to any previous build instantly. Review what worked and what didn\'t.',
    color: 'slate', // History uses neutral slate
    gradient: 'from-slate-500/20 to-slate-600/10',
    accentColor: 'text-slate-400',
    borderColor: 'border-slate-400/20',
  },
];

export default function Features() {
  return (
    <div className="px-6 py-20">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-20 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">Every feature designed for AI-assisted development</h1>
          <p className="mt-6 text-base sm:text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
            Native macOS app that turns xcodebuild chaos into structured reports. Built for developers who use ChatGPT, Claude, Cursor, and Windsurf.
          </p>
        </div>

        {/* Feature Sections - Alternating Layout */}
        <div className="space-y-32">
          {features.map((feature, idx) => {
            const isEven = idx % 2 === 0;
            
            return (
              <div
                key={feature.title}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}
              >
                {/* Screenshot Placeholder */}
                <div className="flex-1 w-full">
                  <div className={`glass-elevated rounded-3xl p-2 border ${feature.borderColor} overflow-hidden`}>
                    <div className={`aspect-video rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center relative overflow-hidden`}>
                      {/* Placeholder content */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
                      <div className="relative text-center p-8">
                        <div className={`inline-flex rounded-2xl bg-gradient-to-br ${feature.gradient} p-6 ${feature.accentColor} mb-4`}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                          </svg>
                        </div>
                        <p className="text-sm text-slate-500 font-medium">Screenshot Coming Soon</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feature Content */}
                <div className="flex-1">
                  <div className={`inline-flex rounded-2xl bg-gradient-to-br ${feature.gradient} px-4 py-2 ${feature.accentColor} text-sm font-semibold mb-4`}>
                    {feature.color.toUpperCase()}
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{feature.title}</h2>
                  <p className="text-lg text-slate-400 leading-relaxed mb-6">
                    {feature.desc}
                  </p>
                  <div className={`glass-card rounded-2xl p-6 border ${feature.borderColor}`}>
                    <div className="flex items-start gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className={`w-5 h-5 ${feature.accentColor} shrink-0 mt-0.5`}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-slate-300">
                        {idx === 0 && 'Exports to BUILD_REPORT.md with full code context'}
                        {idx === 1 && 'Groups violations by rule with severity counts'}
                        {idx === 2 && 'Real-time streaming with syntax highlighting'}
                        {idx === 3 && 'Shows assertion messages with ±3 lines of code'}
                        {idx === 4 && 'Scans Swift version, LOC, architecture patterns'}
                        {idx === 5 && 'Categorizes by OS with health status indicators'}
                        {idx === 6 && 'Combines all issues in one markdown file'}
                        {idx === 7 && 'Tracks status, duration, and timestamp for each build'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Before/After Comparison */}
        <div className="mt-32 border-t border-white/5 pt-32">
          <div className="text-center mb-16 px-4">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">The problem with terminal builds</h2>
            <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
              You run xcodebuild. It fails. You scroll through 5,000 lines looking for the error. You copy it to ChatGPT. You lose the context. You start over.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="glass-card rounded-3xl p-8">
              <div className="text-red-400 text-sm font-semibold mb-3">❌ Without SwiftDev Toolkit</div>
              <ul className="space-y-3 text-slate-400 text-sm leading-relaxed">
                <li>• Scroll through thousands of lines of terminal output</li>
                <li>• Manually copy error messages one by one</li>
                <li>• Lose code context around errors</li>
                <li>• Re-run builds to find warnings you missed</li>
                <li>• Paste incomplete info to AI assistants</li>
                <li>• Wait for AI to ask clarifying questions</li>
              </ul>
            </div>
            
            <div className="glass-elevated rounded-3xl p-8 border-brand-400/20">
              <div className="text-brand-400 text-sm font-semibold mb-3">✓ With SwiftDev Toolkit</div>
              <ul className="space-y-3 text-slate-300 text-sm leading-relaxed">
                <li>• Click "Build" — errors appear in a visual dashboard</li>
                <li>• Toggle to AI View — markdown with code context</li>
                <li>• Click "Copy All" — everything in your clipboard</li>
                <li>• Paste to ChatGPT/Claude — full context included</li>
                <li>• Get fixes in seconds, not minutes</li>
                <li>• Ship faster, debug smarter</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="glass-elevated rounded-3xl p-12 inline-block">
            <p className="text-lg text-slate-300 mb-6">
              Ready to experience the difference?
            </p>
            <AppStoreBadge />
          </div>
        </div>
      </div>
    </div>
  );
}
